import { useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { saveMarketCondition } from '@/utils/localStorage';
import { calculateMarketQualityScore, getMarketQualityMessage } from '@/utils/calculations';
import { toast } from 'sonner';
import { TrendingUp, AlertCircle } from 'lucide-react';

export default function DailyMarketConditions() {
  const [asset, setAsset] = useState('');
  const [timeframe, setTimeframe] = useState('');
  const [analysis, setAnalysis] = useState('');
  const [checklist, setChecklist] = useState({
    marketTrending: false,
    clearDirection: false,
    volatilitySufficient: false,
    noMajorNews: false,
    liquidityHigh: false
  });
  const [score, setScore] = useState(null);
  
  const handleChecklistChange = (key) => {
    setChecklist(prev => ({ ...prev, [key]: !prev[key] }));
  };
  
  const handleCalculate = () => {
    if (!asset || !timeframe) {
      toast.error('Please enter asset and timeframe');
      return;
    }
    
    const calculatedScore = calculateMarketQualityScore(checklist, analysis);
    setScore(calculatedScore);
    
    try {
      saveMarketCondition({
        asset,
        timeframe,
        analysis,
        checklist,
        score: calculatedScore
      });
      toast.success('Market condition saved');
    } catch (error) {
      toast.error('Failed to save market condition');
    }
  };
  
  const handleReset = () => {
    setAsset('');
    setTimeframe('');
    setAnalysis('');
    setChecklist({
      marketTrending: false,
      clearDirection: false,
      volatilitySufficient: false,
      noMajorNews: false,
      liquidityHigh: false
    });
    setScore(null);
  };
  
  const scoreMessage = score !== null ? getMarketQualityMessage(score) : null;
  
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-8">
        <h1 className="text-4xl md:text-5xl font-black tracking-tighter text-foreground mb-2">
          Daily Market Conditions
        </h1>
        <p className="text-muted-foreground font-medium">
          Assess market quality before trading. Prevent bad trades rather than predict price.
        </p>
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="p-6 border-border/40 bg-card">
          <div className="space-y-6">
            <div>
              <Label htmlFor="asset" className="text-foreground font-semibold mb-2 block">
                Asset (Stock/Forex/Crypto)
              </Label>
              <Input
                id="asset"
                data-testid="asset-input"
                value={asset}
                onChange={(e) => setAsset(e.target.value)}
                placeholder="e.g., EURUSD, BTC/USD, AAPL"
                className="h-11 bg-secondary/20 border-border/50 focus:border-primary"
              />
            </div>
            
            <div>
              <Label htmlFor="timeframe" className="text-foreground font-semibold mb-2 block">
                Timeframe
              </Label>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger data-testid="timeframe-select" className="h-11 bg-secondary/20 border-border/50">
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="5m">5 Minutes</SelectItem>
                  <SelectItem value="15m">15 Minutes</SelectItem>
                  <SelectItem value="30m">30 Minutes</SelectItem>
                  <SelectItem value="1h">1 Hour</SelectItem>
                  <SelectItem value="4h">4 Hours</SelectItem>
                  <SelectItem value="1d">Daily</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label htmlFor="analysis" className="text-foreground font-semibold mb-2 block">
                Analysis Notes (Optional)
              </Label>
              <Textarea
                id="analysis"
                data-testid="analysis-textarea"
                value={analysis}
                onChange={(e) => setAnalysis(e.target.value)}
                placeholder="Paste AI analysis or manual notes about trend, volatility, news..."
                rows={4}
                className="bg-secondary/20 border-border/50 focus:border-primary resize-none"
              />
            </div>
            
            <div className="space-y-4 pt-2">
              <h3 className="font-semibold text-lg text-foreground">Market Quality Checklist</h3>
              
              {[
                { key: 'marketTrending', label: 'Market trending' },
                { key: 'clearDirection', label: 'Clear direction' },
                { key: 'volatilitySufficient', label: 'Volatility sufficient' },
                { key: 'noMajorNews', label: 'No major news risk' },
                { key: 'liquidityHigh', label: 'Liquidity high' }
              ].map(item => (
                <div key={item.key} className="flex items-center justify-between py-3 px-4 rounded-md bg-secondary/30 border border-border/30">
                  <Label htmlFor={item.key} className="text-foreground font-medium cursor-pointer flex-1">
                    {item.label}
                  </Label>
                  <Switch
                    id={item.key}
                    data-testid={`checklist-${item.key}`}
                    checked={checklist[item.key]}
                    onCheckedChange={() => handleChecklistChange(item.key)}
                  />
                </div>
              ))}
            </div>
            
            <div className="flex space-x-3 pt-4">
              <Button
                onClick={handleCalculate}
                data-testid="calculate-score-btn"
                className="flex-1 bg-primary hover:bg-primary/90 text-primary-foreground font-semibold h-11"
              >
                <TrendingUp className="w-4 h-4 mr-2" />
                Calculate Score
              </Button>
              <Button
                onClick={handleReset}
                data-testid="reset-btn"
                variant="outline"
                className="h-11 border-border hover:bg-accent"
              >
                Reset
              </Button>
            </div>
          </div>
        </Card>
        
        <div>
          {score !== null && scoreMessage && (
            <Card className={`p-8 border ${scoreMessage.borderColor} ${scoreMessage.bgColor}`}>
              <div className="text-center space-y-6">
                <div>
                  <h2 className="text-2xl font-bold text-muted-foreground mb-2">Trade Quality Score</h2>
                  <div className="text-7xl font-black font-mono tracking-tighter" style={{ color: scoreMessage.color.replace('text-', '#') === 'text-destructive' ? '#EF4444' : scoreMessage.color.replace('text-', '#') === 'text-warning' ? '#F59E0B' : '#10B981' }}>
                    {score}
                  </div>
                  <p className="text-sm text-muted-foreground mt-1">out of 100</p>
                </div>
                
                <div className={`inline-flex items-center space-x-2 px-6 py-3 rounded-full ${scoreMessage.bgColor} border ${scoreMessage.borderColor}`}>
                  <AlertCircle className={`w-5 h-5 ${scoreMessage.color}`} />
                  <span className={`font-bold text-lg ${scoreMessage.color}`}>
                    {scoreMessage.message}
                  </span>
                </div>
                
                <div className="pt-6 space-y-3 text-left">
                  <h3 className="font-semibold text-foreground text-lg">Checklist Summary</h3>
                  <div className="space-y-2">
                    {Object.entries(checklist).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-between py-2 px-3 rounded bg-background/50">
                        <span className="text-sm text-muted-foreground capitalize">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </span>
                        <span className={`font-semibold text-sm ${
                          value ? 'text-success' : 'text-destructive'
                        }`}>
                          {value ? '✓' : '✗'}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          )}
          
          {score === null && (
            <Card className="p-8 border-border/40 bg-card h-full flex items-center justify-center">
              <div className="text-center text-muted-foreground">
                <TrendingUp className="w-16 h-16 mx-auto mb-4 opacity-20" />
                <p className="font-medium">Fill in the form and calculate your market quality score</p>
              </div>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}